import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Form,
  Input,
  Popconfirm,
  Select,
  Spin,
  Table,
  Tag,
  Typography,
} from "antd";
import { DeleteOutlined, SearchOutlined } from "@ant-design/icons";
import {
  showErrorNotification,
  showSuccessNotification,
} from "../../util/notifications";
import {
  UserFeaturePermissionDto,
  UserFeaturePermissionResponse,
  assignUserPermissions,
  getUserPermissions,
  removeUserPermission,
} from "./sagas";
import { useFeaturePermissionContext } from "../../context/FeaturePermissionContext";
import Unauthorized from "../unauthorized/unauthorized";

const { Title, Text } = Typography;

interface AssignFormValues {
  userEmail: string;
  featureNames: string[];
}

const PermissionManagement: React.FC = () => {
  const { isAdmin, loaded } = useFeaturePermissionContext();
  const [form] = Form.useForm<AssignFormValues>();
  const [permissions, setPermissions] = useState<UserFeaturePermissionDto[]>([]);
  const [assignableFeatures, setAssignableFeatures] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [removingId, setRemovingId] = useState<number | null>(null);
  const [searchText, setSearchText] = useState<string>("");

  const applyResponse = (response: UserFeaturePermissionResponse) => {
    setPermissions(response.Permissions || []);
    setAssignableFeatures(response.AssignableFeatures || []);
  };

  const loadPermissions = async () => {
    setLoading(true);
    try {
      applyResponse(await getUserPermissions());
    } catch (error: any) {
      showErrorNotification("Unable to load permissions", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      loadPermissions();
    }
  }, [isAdmin]);

  const handleAssign = async (values: AssignFormValues) => {
    setSaving(true);
    try {
      const response = await assignUserPermissions(
        values.userEmail.trim(),
        values.featureNames
      );
      applyResponse(response);
      form.resetFields();
      showSuccessNotification(
        `Write access granted to ${values.userEmail.trim()}`
      );
    } catch (error: any) {
      showErrorNotification("Unable to assign permission", error);
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (record: UserFeaturePermissionDto) => {
    setRemovingId(record.Id);
    try {
      applyResponse(await removeUserPermission(record.Id));
      showSuccessNotification(
        `Removed ${record.FeatureName} access for ${record.UserEmail}`
      );
    } catch (error: any) {
      showErrorNotification("Unable to remove permission", error);
    } finally {
      setRemovingId(null);
    }
  };

  const filteredPermissions = useMemo(() => {
    const search = searchText.toLowerCase();
    return permissions.filter(
      (p) =>
        p.UserEmail.toLowerCase().includes(search) ||
        p.FeatureName.toLowerCase().includes(search)
    );
  }, [permissions, searchText]);

  if (!loaded) {
    return <Spin style={{ display: "block", margin: 40 }} />;
  }

  if (!isAdmin) {
    return <Unauthorized />;
  }

  const columns = [
    {
      title: "User Email",
      dataIndex: "UserEmail",
      key: "UserEmail",
      sorter: (a: UserFeaturePermissionDto, b: UserFeaturePermissionDto) =>
        a.UserEmail.localeCompare(b.UserEmail),
    },
    {
      title: "Feature",
      dataIndex: "FeatureName",
      key: "FeatureName",
      sorter: (a: UserFeaturePermissionDto, b: UserFeaturePermissionDto) =>
        a.FeatureName.localeCompare(b.FeatureName),
    },
    {
      title: "Access",
      dataIndex: "Permission",
      key: "Permission",
      render: (permission: string) => <Tag color="blue">{permission}</Tag>,
    },
    {
      title: "Feature Status",
      dataIndex: "IsFeatureEnabled",
      key: "IsFeatureEnabled",
      render: (isEnabled: boolean) =>
        isEnabled ? (
          <Tag color="green">Enabled</Tag>
        ) : (
          <Tag color="red">Disabled (inactive)</Tag>
        ),
    },
    {
      title: "Assigned By",
      dataIndex: "CreatedBy",
      key: "CreatedBy",
    },
    {
      title: "Action",
      key: "action",
      render: (_: any, record: UserFeaturePermissionDto) => (
        <Popconfirm
          title={`Remove ${record.FeatureName} access for ${record.UserEmail}?`}
          okText="Remove"
          onConfirm={() => handleRemove(record)}
        >
          <Button
            danger
            type="link"
            icon={<DeleteOutlined />}
            loading={removingId === record.Id}
          >
            Remove
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <Card style={{ margin: 16 }}>
      <Title level={4} style={{ marginTop: 0 }}>
        Permission Management
      </Title>
      <Text type="secondary">
        Grant Write access on enabled features to a user. Timer/Queue and
        disabled features cannot be assigned.
      </Text>

      <Form
        form={form}
        layout="inline"
        onFinish={handleAssign}
        style={{ marginTop: 16, rowGap: 8 }}
      >
        <Form.Item
          name="userEmail"
          rules={[
            { required: true, message: "User email is required" },
            { type: "email", message: "Enter a valid email" },
          ]}
        >
          <Input placeholder="User email" style={{ width: 280 }} />
        </Form.Item>
        <Form.Item
          name="featureNames"
          rules={[{ required: true, message: "Select at least one feature" }]}
        >
          <Select
            mode="multiple"
            allowClear
            placeholder="Select features"
            style={{ minWidth: 360, maxWidth: 600 }}
            options={assignableFeatures.map((f) => ({ label: f, value: f }))}
            maxTagCount="responsive"
          />
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit" loading={saving}>
            Grant Write Access
          </Button>
        </Form.Item>
        <Form.Item style={{ marginLeft: "auto", marginRight: 0 }}>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Search by user or feature"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 320 }}
          />
        </Form.Item>
      </Form>

      <Spin spinning={loading}>
        <Table
          style={{ marginTop: 16 }}
          rowKey="Id"
          columns={columns}
          dataSource={filteredPermissions}
          pagination={{ pageSize: 20 }}
        />
      </Spin>
    </Card>
  );
};

export default PermissionManagement;
